import React from 'react';
import { connect } from 'react-redux';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import { patientListFetch, patientListSetPage, patientListUnload } from '../../lib/redux/actions/patients';
import MASpinner from '../../components/common/MASpinner';
import Paginator from '../../components/common/Paginator';
import * as ROUTES from '../../lib/routing/frontend';
import PatientListTable from './PatientListTable';
import PatientTableFilterForm from '../../components/forms/Patient/PatientTableFilterForm';
import { getFiltersFromSession } from '../../lib/routing/filters';

const mapStateToProps = (state) => ({
  ...state.patients,
});

const mapDispatchToProps = {
  patientListFetch,
  patientListSetPage,
  patientListUnload,
};

/**
 * MED-921 - the patient index.
 *
 * Paginated and searched server-side, unlike the hospital and consultant lists which pull
 * everything and filter in the browser. That pattern does not transfer: this register holds
 * 57,624 people.
 *
 * Filters open by default rather than behind a toggle. On a list this size the search box is the
 * screen - scrolling 1,921 pages to find someone is not a thing anyone will do.
 */
class PatientListContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showFilters: true,
    };
  }

  getCombinedFilters() {
    const { filters, defaultFilters = {}, match } = this.props;
    const filtersFromSession = getFiltersFromSession(match.path, 'json');

    const combined = { ...filters, ...defaultFilters, ...filtersFromSession };

    // A cleared filter is carried as null so it overrides a stale session value; the API should
    // not be sent the key at all.
    Object.keys(combined).forEach((key) => {
      if (combined[key] === null || combined[key] === undefined || combined[key] === '') {
        delete combined[key];
      }
    });

    return combined;
  }

  componentDidMount() {
    this.props.patientListFetch(this.getQueryParamPage(), this.getCombinedFilters());
  }

  componentWillUnmount() {
    this.props.patientListUnload();
  }

  componentDidUpdate(prevProps) {
    const { currentPage, patientListFetch, patientListSetPage, filters } = this.props;

    if (prevProps.match.params.page !== this.getQueryParamPage()) {
      patientListSetPage(this.getQueryParamPage());
    }
    if (prevProps.currentPage !== currentPage || prevProps.filters !== filters) {
      patientListFetch(currentPage, this.getCombinedFilters());
    }
  }

  getQueryParamPage() {
    return Number(this.props.match.params.page) || 1;
  }

  changePage(page) {
    const { history, patientListSetPage } = this.props;
    patientListSetPage(page);
    history.push(ROUTES.PATIENTS.ALL.replace(':page', page));
  }

  nextPage() {
    const { currentPage, pageCount } = this.props;
    this.changePage(Math.min(currentPage + 1, pageCount));
  }

  prevPage() {
    const { currentPage } = this.props;
    this.changePage(Math.max(currentPage - 1, 1));
  }

  toggleFilters() {
    this.setState((prevState) => ({ showFilters: !prevState.showFilters }));
  }

  render() {
    const { patients, isFetchingPatients, pageTitle, pageCount, currentPage } = this.props;
    const hasPatients = patients && patients.length > 0;

    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between">
            {pageTitle}
            <div>
              <button className="btn btn-sm btn-outline-secondary mr-3" onClick={this.toggleFilters.bind(this)}>
                Filters
              </button>
              {!isFetchingPatients && hasPatients && <span className="tabular-data-result-count">{pageCount} pages</span>}
            </div>
          </div>
        </Card.Header>

        {this.state.showFilters && (
          <Card.Body className={'mediaccount-filter-wrapper'}>
            <PatientTableFilterForm location={this.props.match.path} />
          </Card.Body>
        )}

        {isFetchingPatients && (
          <Card.Body>
            <MASpinner />
          </Card.Body>
        )}

        {!isFetchingPatients && !hasPatients && (
          <Card.Body>
            <Alert variant={'info'} className="mb-0">
              <i className="fas fa-info-circle" /> No patients found
            </Alert>
          </Card.Body>
        )}

        {!isFetchingPatients && hasPatients && <PatientListTable patients={patients} />}

        {!isFetchingPatients && hasPatients && (
          <Paginator
            currentPage={currentPage}
            pageCount={pageCount}
            setPage={this.changePage.bind(this)}
            nextPage={this.nextPage.bind(this)}
            prevPage={this.prevPage.bind(this)}
          />
        )}
      </Card>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PatientListContainer);
